<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import InputText from 'primevue/inputtext'
import ProgressSpinner from 'primevue/progressspinner'
import GbButton from '@/components/GbButton.vue'
import CopyButton from '@/components/CopyButton.vue'
import { useLibraryStore } from './library.store'
import { useVisibilityStore } from '@/visibility/visibility.store'

defineProps<{
  isShared: boolean
  shareLink: string
  isSharing: boolean
  linkLabel: string
  showCopiedCheck?: boolean
}>()

const emit = defineEmits<{
  share: []
  unshare: []
  copied: []
}>()

const { shareVisibility, editorVisibility } = storeToRefs(useVisibilityStore())
const { currentFolderUserRole } = storeToRefs(useLibraryStore())

const shouldShowPanel = computed(() =>
  shareVisibility.value && currentFolderUserRole.value !== '' && currentFolderUserRole.value !== 'contributor'
)

function selectLink(event: FocusEvent) {
  (event.target as HTMLInputElement).select()
}
</script>

<template>
  <div v-if="shouldShowPanel" class="rounded-xs border border-gray-300 bg-green-100 p-5 text-center">
    <h3 class="mb-1 text-left text-text-xl font-medium text-green-300">Send to caregivers or colleagues</h3>
    <GbButton
      v-if="!isShared"
      type="button"
      gb-theme="bluePrimary"
      size="small"
      class="min-h-10 w-full"
      :disabled="isSharing"
      @click="emit('share')"
    >
      <template v-if="isSharing">
        <ProgressSpinner stroke-width="6" class="!mr-1 !ml-0 h-4 w-4 stroke-white" aria-hidden="true" />
        Loading...
      </template>
      <template v-else>
        <span class="material-icons-round mr-1" aria-hidden="true">link</span>
        Create a View-only Link
      </template>
    </GbButton>
    <div v-else>
      <div class="mb-2 rounded-xs bg-blue-400 px-2 py-2.5 text-white">
        <span class="material-icons-round mr-1 align-text-bottom text-text-md" aria-hidden="true">visibility</span>
        Anyone with the link can view
        <button
          v-if="editorVisibility"
          type="button"
          class="block w-full text-text-sm text-blue-200 hover:underline"
          @click="emit('unshare')"
        >
          Make private
        </button>
      </div>
      <div class="mb-2 flex items-stretch">
        <span class="flex items-center rounded-l-xs border border-r-0 border-gray-400 bg-gray-200 px-3" aria-hidden="true">
          <span class="material-icons-round text-text-md text-gray-900">link</span>
        </span>
        <InputText
          class="grow rounded-l-none"
          readonly
          :value="shareLink"
          type="text"
          :aria-label="linkLabel"
          @focus="selectLink"
        />
      </div>
      <CopyButton
        :string-to-copy="shareLink"
        button-text="Copy Link"
        tooltip-text="Link copied!"
        button-theme="grayOutlineSecondary"
        size="small"
        custom-class="w-full"
        :show-copied-check="showCopiedCheck"
        @copied="emit('copied')"
      >
        <template #icon>
          <span class="material-icons-round mr-1" aria-hidden="true">link</span>
        </template>
      </CopyButton>
    </div>
  </div>
</template>
